//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import React from 'react';
import { useSelector } from 'react-redux';
import styled, { css, useTheme } from 'styled-components';

import { RightsRequestCommunicationType } from '@apis/rights-management/rightsManagementApiModels';
import Icon from '@components/atoms/icon/Icon';
import { BodyText } from '@components/atoms/text/Text';
import getGalleriesClientType from '@galleries/selectors/getGalleriesClientType';
import { Mixins } from '@theme/mixins';
import {
  Plurality,
  getFeatureName,
} from '@uia/rights/constants/getFeatureNames';
import { RIGHTS_REQUEST_EXTERNAL_APPROVAL_COMMUNICATION_TYPES } from '@uia/rights/constants/uiaRightsConstants';
import { getIsRenderedWithinAnotherWorkflow } from '@uia/rights/selectors/getUIARights';
import { isDefined } from '@utils/filterPredicates';

import { RIGHTS_STATUS } from 'app/bulk-actions/bulk-actions-constants/bulk-actions-constants';

export interface RightsApprovalPhaseProps {
  approvalPhrase: string | undefined;
  communicationType?: RightsRequestCommunicationType;
  status: RIGHTS_STATUS;
}

export const ApprovalPhraseMessage = ({
  approvalPhrase,
  communicationType,
  status,
}: RightsApprovalPhaseProps) => {
  const theme = useTheme();

  const isRenderedWithinAnotherWorkflow = useSelector(
    getIsRenderedWithinAnotherWorkflow,
  );

  const galleriesClientType = useSelector(getGalleriesClientType);
  const galleriesText = getFeatureName(galleriesClientType, Plurality.default);

  const hasExternalRightsApproval =
    isDefined(communicationType) &&
    RIGHTS_REQUEST_EXTERNAL_APPROVAL_COMMUNICATION_TYPES.includes(
      communicationType,
    ) &&
    status === RIGHTS_STATUS.APPROVED;

  return (
    <>
      {(isDefined(approvalPhrase) || hasExternalRightsApproval) && (
        <>
          {RIGHTS_STATUS.APPROVED !== status && (
            <Text>
              <StyledIcon
                $color={theme.colors.aliases.bodySecondary}
                name='info'
              />
              &nbsp;
              <span>
                Your content will be approved when the user replies with&nbsp;
                <Bold>{approvalPhrase}</Bold>.
              </span>
            </Text>
          )}
          {!isRenderedWithinAnotherWorkflow &&
            !hasExternalRightsApproval &&
            galleriesText.length > 0 && (
              <PrimaryText>
                <StyledIcon name='info' />
                &nbsp;
                <span>
                  Upon approval, this image will not be distributed to&nbsp;
                  {galleriesText}.
                </span>
              </PrimaryText>
            )}
        </>
      )}
    </>
  );
};

const Text = styled(BodyText)`
  display: flex;
  align-items: center;
  justify-content: start;
`;

const PrimaryText = styled(BodyText)(
  ({ theme }) => `
    display: flex;
    align-items: center;
    justify-content: start;
    color: ${theme.colors.aliases.primary};
  `,
);

const Bold = styled.span`
  ${Mixins.Text.BodyBold}
`;

const StyledIcon = styled(Icon)<{ $color?: string }>(
  ({ $color }) => css`
    ${isDefined($color) ? `color: ${$color};` : ''}
  `,
);
